
import { useCallback, useMemo, useState } from "react";
import CategoryTable from "../components/CategoryTable";
import { applyPagination } from "../../utils/applyPagination";
import Sidepane from "../components/Sidepane";
import CategoryDetails from "../../dashboard/components/CategoryDetail";
import type { ICategory } from "../../entities/category.entity";
import { useSidePane } from "../hook/useSidePane";
import { useCategoriesList } from "../hook/useCategoriesList";
import { useCategoryRemover } from "../hook/useCategoryRemover";
import { CrudContainer } from "../components/CrudContainer";

const useCategoriesPaginated = (
	page: number,
	rowsPerPage: number,
	data: unknown,
) => {
	return useMemo(() => {
		return applyPagination(data, page, rowsPerPage);
	}, [page, rowsPerPage, data]);
};

export default function Categories() {
	const [categoryDetail, setCategoryDetail] = useState<ICategory | undefined>(
		undefined,
	);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const categoriesQuery = useCategoriesList();
	const categories = useCategoriesPaginated(
		page,
		rowsPerPage,
		categoriesQuery.data || [],
	);
	const sidepane = useSidePane({
		onClose: () => setCategoryDetail(undefined),
	});

	const categoryRemover = useCategoryRemover();

	const handlePageChange = useCallback<
		NonNullable<Parameters<typeof CategoryTable>[0]["onPageChange"]>
	>((_, value: number) => {
		setPage(value);
	}, []);

	const handleRowsPerPageChange = useCallback<
		NonNullable<Parameters<typeof CategoryTable>[0]["onRowsPerPageChange"]>
	>((event) => {
		setRowsPerPage(event.target.value);
	}, []);

	function handleUpdateCategory(id: string) {
		if (!categoriesQuery.data) return;

		for (const category of categoriesQuery.data) {
			if (category.id === id) {
				setCategoryDetail(category);

				sidepane.setOpen(true);
			}
		}
	}

	const handleCategorySaved = async () => {
		sidepane.setOpen(false);
	};

	return (
		<>
			<CrudContainer
				title="Categorias"
				onClickAdd={() => sidepane.setOpen(true)}
				empty={categoriesQuery.data?.length === 0}
			>
				{categories.length > 0 && (
					<CategoryTable
						count={categoriesQuery.data?.length || 0}
						items={categories}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						categoryRemover={categoryRemover}
						onUpdateCategory={handleUpdateCategory}
						page={page}
						rowsPerPage={rowsPerPage}
					/>
				)}
			</CrudContainer>

			<Sidepane open={sidepane.isOpen} toggle={sidepane.setOpen}>
				<CategoryDetails
					item={categoryDetail}
					onCategorySaved={handleCategorySaved}
				/>
			</Sidepane>
		</>
	);
}
